/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@iconify/react';
import * as XLSX from 'xlsx';
import { getPaginatedPurchaseOrders, createPurchaseOrder, updatePurchaseOrder, deletePurchaseOrder, getPurchaseOrderById } from '../api/purchase-orderAPI';
import { getPurchaseOrderLinesByPurchaseOrderId, createPurchaseOrderLine, updatePurchaseOrderLine, deletePurchaseOrderLine } from '../api/purchase-order-linesAPI';
import { getHierarchyStructureById } from '../api/hierarchy-structureAPI';
import { useFormBuilder } from '../utils/useFormBuilder';
import { useTableFilters } from '../utils/useTableFilters';
import { validateForm } from '../utils/useValidator';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import PurchaseOrderPanelView from './PurchaseOrderPanelView';
import exportPurchaseOrderPdf from '../utils/exportPurchaseOrderPdf';
import { getPurchaseOrderStatus } from '../api/purchase-order-statusAPI';
import PurchaseOrderDataTable from './PurchaseOrderDataTable';
import { createSupplier, getPaginatedSuppliers } from '../api/supplierAPI';
import { useSettings } from '../utils/AppSettingsContext';

// Purchase order status options loaded from API
const statusOptionsInitial = [];

const PurchaseOrderLayer = ({ viewMode = 'table', initialPurchaseOrderNumber = '' }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [headerSearchContainer, setHeaderSearchContainer] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [newOrder, setNewOrder] = useState({});
  const [errors, setErrors] = useState({});
  const [formReadOnly, setFormReadOnly] = useState(false);
  const [orderLines, setOrderLines] = useState([]);
  const [editingLineIndex, setEditingLineIndex] = useState(null);
  const [lineForm, setLineForm] = useState({});
  const [lineErrors, setLineErrors] = useState({});
  const [deletedLineIds, setDeletedLineIds] = useState([]);
  const [statusOptions, setStatusOptions] = useState(statusOptionsInitial);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState(null);
  const userProfile = useUserProfile();
  const defaultCurrency = useSettings('default_currency');
  const defaultPaymentTerms = useSettings('default_payment_terms');
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [newSupplier, setNewSupplier] = useState({ currencyId: defaultCurrency || 1 });
  const [supplierErrors, setSupplierErrors] = useState({});
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  const handleSupplierSearch = useCallback(async (inputValue = '', { page = 1 } = {}) => {
    try {
      const trimmedSearch = typeof inputValue === 'string' ? inputValue.trim() : '';
      const initialPageSize = 10;
      const lazyPageSize = 5;
      const pageSize = page === 1 ? initialPageSize : lazyPageSize;
      const apiPage = page === 1 ? 1 : page + 1;
      const params = { page: apiPage, pageSize };

      if (trimmedSearch) {
        params.search = trimmedSearch;
      }

      const result = await getPaginatedSuppliers(params);
      const items = result.data || result;
      const options = items.map((supplier) => ({
        value: supplier.id,
        label: supplier.name,
        raw: supplier,
      }));
      const addOption = { value: 'add', label: '+ Add Supplier' };
      const nextOptions = page === 1 ? [addOption, ...options] : options;

      const total =
        typeof result.total === 'number'
          ? result.total
          : typeof result.count === 'number'
            ? result.count
            : undefined;
      const loadedCount =
        page === 1
          ? initialPageSize
          : initialPageSize + (page - 1) * lazyPageSize;
      const hasMore =
        typeof total === 'number'
          ? loadedCount < total
          : options.length === pageSize;

      return { options: nextOptions, hasMore };
    } catch (err) {
      console.error('Error fetching suppliers:', err);
      return { options: [{ value: 'add', label: '+ Add Supplier' }], hasMore: false };
    }
  }, []);

  useEffect(() => {
    setHeaderSearchContainer(document.getElementById('header-search-container'));
  }, []);

  // Load purchase order statuses from API
  useEffect(() => {
    (async () => {
      try {
        const data = await getPurchaseOrderStatus();
        const opts = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
            ? data.data
            : [];
        setStatusOptions(
          opts.map((s) => ({
            value: s.id,
            label: s.status,
            colour: s.colours?.bootstrapName,
          }))
        );
      } catch (err) {
        console.error('Failed to load purchase order statuses', err);
      }
    })();
  }, []);

  // Recalculate total amount whenever purchase order lines change
  useEffect(() => {
    if (orderLines.length > 0) {
      const total = orderLines.reduce((sum, l) => sum + Number(l.lineTotal || 0), 0);
      setNewOrder((prev) => ({ ...prev, totalAmount: parseFloat(total.toFixed(2)) }));
    }
  }, [orderLines]);

  // When department changes, populate delivery details if available
  useEffect(() => {
    if (!newOrder.departmentId) return;
    (async () => {
      try {
        const dep = await getHierarchyStructureById(newOrder.departmentId);
        if (!dep) return;
        setNewOrder((prev) => ({
          ...prev,
          deliveryAddress:
            prev.deliveryAddress || [dep.deliveryAddressL1, dep.deliveryAddressL2].filter(Boolean).join(' '),
          deliveryCity: prev.deliveryCity || dep.deliveryCity || '',
          deliveryState: prev.deliveryState || dep.deliveryState || '',
          deliveryZip: prev.deliveryZip || dep.deliveryPostCode || '',
          deliveryPhone: prev.deliveryPhone || dep.deliveryPhone || '',
        }));
      } catch (err) {
        console.error('Failed to load department details', err);
      }
    })();
  }, [newOrder.departmentId]);

  const listContainerRef = useRef(null);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [poPage, setPoPage] = useState(1);
  const [poHasMore, setPoHasMore] = useState(true);
  const [poLoading, setPoLoading] = useState(false);
  // Track ongoing fetches to prevent outdated responses causing duplicates
  const fetchIdRef = useRef(0);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedOrderLines, setSelectedOrderLines] = useState([]);

  const inputFields = useMemo(
    () => [
      { field: 'poNumber', label: 'PO Number', type: 'text', size: '6' },
      {
        field: 'supplierId',
        label: 'Supplier',
        type: 'paginated-select',
        size: '6',
        loadOptions: handleSupplierSearch,
        selectedOption: selectedSupplier,
        setSelectedOption: setSelectedSupplier,
        isClearable: true,
        minSearchLength: 2,
        required: true,
        onChange: (opt, setFormData, field, setSelectedOptionValue) => {
          if (opt?.value === 'add') {
            setNewSupplier({ currencyId: parseInt(defaultCurrency, 10) || 1, paymentTerms: defaultPaymentTerms || '' });
            setSupplierErrors({});
            setShowSupplierModal(true);
            setFormData(prev => ({ ...prev, [field]: null }));
            if (setSelectedOptionValue) {
              setSelectedOptionValue(null);
            }
          } else {
            setFormData(prev => ({ ...prev, [field]: opt?.value || null }));
            if (setSelectedOptionValue) {
              setSelectedOptionValue(opt ?? null);
            }
          }
        },
      },
      { field: 'departmentId', label: 'Department', type: 'select', size: '6', api: 'hierarchy-structures', displayField: 'name', required: 'true' },
      { field: 'orderDate', label: 'Order Date', type: 'date', size: '6', required: true },
      { field: 'deliveryDate', label: 'Delivery Date', type: 'date', size: '6' },
      { field: 'deliveryContact', label: 'Delivery Contact', type: 'text', size: '6', required: true },
      { field: 'deliveryPhone', label: 'Delivery Phone', type: 'text', size: '6' },
      { field: 'deliveryAddress', label: 'Delivery Address', type: 'text', size: '6', required: true },
      { field: 'deliveryCity', label: 'Delivery City', type: 'text', size: '6' },
      { field: 'deliveryState', label: 'Delivery State', type: 'text', size: '6' },
      { field: 'deliveryZip', label: 'Delivery Post Code', type: 'text', size: '6' },
      { field: 'statusId', label: 'Status', type: 'select', size: '6', options: statusOptions, required: true },
      // Disable Total Amount editing when there are purchase order lines
      { field: 'totalAmount', label: 'Total Amount', type: 'number', size: '6', disabled: orderLines.length > 0 },
      { field: 'notes', label: 'Notes', type: 'textarea', size: '12' },
    ],
    [orderLines.length, statusOptions, handleSupplierSearch, selectedSupplier, defaultCurrency, defaultPaymentTerms],
  );

  const { renderField } = useFormBuilder(inputFields, newOrder, setNewOrder, errors);

  const supplierInputFields = useMemo(
    () => [
      { field: 'name', label: 'Name', type: 'text', size: '6', required: true },
      { field: 'supplierCode', label: 'Code', type: 'text', size: '6' },
      { field: 'address', label: 'Address', type: 'text', size: '6' },
      { field: 'state', label: 'State', type: 'text', size: '6' },
      { field: 'city', label: 'City', type: 'text', size: '6' },
      { field: 'postCode', label: 'Post Code', type: 'text', size: '6' },
      { field: 'phone', label: 'Phone', type: 'text', size: '6' },
      { field: 'paymentTerms', label: 'Payment Terms', type: 'text', size: '6' },
      { field: 'taxNumber', label: 'Tax Number', type: 'text', size: '6' },
      { field: 'currencyId', label: 'Currency', type: 'select', size: '6', api: 'currency', displayField: 'symbol', required: true },
    ],
    [],
  );

  const { renderField: renderSupplierField } = useFormBuilder(
    supplierInputFields,
    newSupplier,
    setNewSupplier,
    supplierErrors,
  );

  const tableFilters = useMemo(
    () => [
      {
        field: 'supplierId',
        type: 'dropDown',
        label: 'Supplier',
        api: 'suppliers',
        displayField: 'name',
        menuBarFilter: 1,
      },
      {
        field: 'status',
        type: 'paginatedDropdown',
        label: 'Status',
        loadOptions: async () => statusOptions,
        menuBarFilter: 1,
      },
      { field: 'startDate', type: 'date', label: 'Start Date' },
      { field: 'endDate', type: 'date', label: 'End Date' },
    ],
    [statusOptions]
  );

  const {
    appliedFilters,
    renderFilterControls,
    resetFilters,
    applyFilters,
    setTempFilter,
  } = useTableFilters(tableFilters);

  const updateOrders = async () => {
    setPurchaseOrders([]);
    setPoPage(1);
    setPoHasMore(true);
    const btn = document.getElementById('apply-filters-btn');
    if (btn) btn.click();
  };

  useEffect(() => {
    if (statusOptions.length === 0) return;
    // default filters for status Draft and Sent
    setTempFilter(
      'status',
      statusOptions.filter((opt) => ['Draft', 'Sent'].includes(opt.label))
    );
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusOptions]);

  // Handle purchase order number passed via query param
  const paramPoNumberRef = useRef(null);
  useEffect(() => {
    if (initialPurchaseOrderNumber) {
      paramPoNumberRef.current = initialPurchaseOrderNumber;
      resetFilters();
      setSearchTerm(initialPurchaseOrderNumber);
      setPurchaseOrders([]);
      setPoPage(1);
      setPoHasMore(true);
    }
  }, [initialPurchaseOrderNumber]);

  useEffect(() => {
    setPurchaseOrders([]);
    setPoPage(1);
    setPoHasMore(true);
  }, [appliedFilters, searchTerm]);

  useEffect(() => {
    setPurchaseOrders([]);
    setPoPage(1);
    setPoHasMore(true);
    setSelectedOrder(null);
  }, [viewMode]);

  useEffect(() => {
    if (viewMode !== 'panel' || notFound) return;
    if (!selectedOrder && purchaseOrders.length > 0) {
      setSelectedOrder(purchaseOrders[0]);
    }
  }, [viewMode, purchaseOrders, selectedOrder, notFound]);

  useEffect(() => {
    if (viewMode !== 'panel' || !selectedOrder || notFound) return;
    getPurchaseOrderLinesByPurchaseOrderId(selectedOrder.id)
      .then(setSelectedOrderLines)
      .catch(() => setSelectedOrderLines([]));
  }, [viewMode, selectedOrder, notFound]);

  useEffect(() => {
    const fetchOrders = async () => {
      setPoLoading(true);
      const fetchId = ++fetchIdRef.current;
      try {
        const pageSize = poPage === 1 ? 25 : 5;
        const resp = await getPaginatedPurchaseOrders({
          page: poPage,
          pageSize,
          search: searchTerm,
          statusId: appliedFilters.status?.map((o) => o.value),
          supplierId: appliedFilters.supplierId,
          startDate: appliedFilters.startDate,
          endDate: appliedFilters.endDate,
        });
        if (fetchId !== fetchIdRef.current) return;
        setPurchaseOrders(prev =>
          poPage === 1 ? resp.data : [...prev, ...resp.data]
        );
        setPoHasMore(poPage * pageSize < resp.total);
      } catch (err) {
        console.error('Error fetching purchase orders:', err);
      } finally {
        if (fetchId === fetchIdRef.current) setPoLoading(false);
      }
    };

    fetchOrders();
  }, [poPage, appliedFilters, searchTerm, viewMode]);

  useEffect(() => {
    if (!paramPoNumberRef.current) return;
    if (poLoading) return;
    const match = purchaseOrders.find(po => po.poNumber === paramPoNumberRef.current);
    if (match) {
      setSelectedOrder(match);
      setNotFound(false);
      paramPoNumberRef.current = null;
    } else if (purchaseOrders.length === 0 && !poLoading) {
      setSelectedOrder(null);
      setNotFound(true);
    } else if (!match && !poLoading && poPage === 1) {
      // if first page loaded and no match
      setSelectedOrder(null);
      setNotFound(true);
    }
  }, [purchaseOrders, poLoading]);

  useEffect(() => {
    const container = listContainerRef.current;
    if (!container) return;

    const onScroll = () => {
      if (
        container.scrollTop + container.clientHeight >= container.scrollHeight - 5 &&
        poHasMore &&
        !poLoading
      ) {
        setPoPage(p => p + 1);
      }
    };

    container.addEventListener('scroll', onScroll);
    return () => container.removeEventListener('scroll', onScroll);
  }, [poHasMore, poLoading, viewMode]);


  const handleLineInputChange = (e) => {
    const { name, value } = e.target;
    const updated = { ...lineForm, [name]: value };
    const base = Number(updated.quantity) * Number(updated.unitPrice);
    const total = base + base * (Number(updated.taxRate) / 100);
    updated.lineTotal = isNaN(total) ? 0 : parseFloat(total.toFixed(2));
    setLineForm(updated);
  };

  const validateLineForm = (form) => {
    const errs = {};
    if (!form.description?.trim()) errs.description = 'Required';
    if (form.quantity === '' || Number(form.quantity) <= 0) errs.quantity = 'Invalid';
    if (form.unitPrice === '' || Number(form.unitPrice) < 0) errs.unitPrice = 'Invalid';
    if (form.taxRate === '' || Number(form.taxRate) < 0) errs.taxRate = 'Invalid';
    return errs;
  };

  const handleAddLine = () => {
    if (formReadOnly) return;
    setLineForm({ itemCode: '', description: '', quantity: 1, unitPrice: 0, taxRate: 0, lineTotal: 0, notes: '' });
    setEditingLineIndex(orderLines.length);
    setOrderLines(prev => [...prev, { id: null, itemCode: '', description: '', quantity: 1, unitPrice: 0, taxRate: 0, lineTotal: 0, notes: '' }]);
  };

  const handleEditLine = (idx) => {
    if (formReadOnly) return;
    setLineForm(orderLines[idx]);
    setEditingLineIndex(idx);
  };

  const handleDeleteLine = (idx) => {
    if (formReadOnly) return;
    if (!window.confirm('Delete line?')) return;
    const line = orderLines[idx];
    if (line.id) setDeletedLineIds(prev => [...prev, line.id]);
    setOrderLines(prev => prev.filter((_, i) => i !== idx));
    if (editingLineIndex === idx) {
      setEditingLineIndex(null);
      setLineErrors({});
    }
  };

  const handleSaveSupplier = async () => {
    const errs = validateForm(supplierInputFields, newSupplier);
    setSupplierErrors(errs);
    if (Object.keys(errs).length) return;
    const clean = {};
    for (const key in newSupplier) {
      if (newSupplier[key] !== null && newSupplier[key] !== '') {
        clean[key] = newSupplier[key];
      }
    }
    try {
      const created = await createSupplier(clean);
      setShowSupplierModal(false);
      setNewOrder(prev => ({ ...prev, supplierId: created.id }));
      setSelectedSupplier({ value: created.id, label: created.name, raw: created });
    } catch (err) {
      console.error('Failed to create supplier', err);
    }
  };

  const handleSaveLine = () => {
    const errs = validateLineForm(lineForm);
    setLineErrors(errs);
    if (Object.keys(errs).length) return;
    const base = Number(lineForm.quantity) * Number(lineForm.unitPrice);
    const total = base + base * (Number(lineForm.taxRate) / 100);
    const updated = { ...lineForm, lineTotal: parseFloat(total.toFixed(2)) };
    setOrderLines(prev => prev.map((l, i) => (i === editingLineIndex ? updated : l)));
    setEditingLineIndex(null);
    setLineForm({});
    setLineErrors({});
  };

  const handleCancelLine = () => {
    if (orderLines[editingLineIndex] && !orderLines[editingLineIndex].id) {
      setOrderLines(prev => prev.filter((_, i) => i !== editingLineIndex));
    }
    setEditingLineIndex(null);
    setLineForm({});
    setLineErrors({});
  };


  const handleEdit = (order) => {
    setFormReadOnly(false);
    setNewOrder(order);
    setSelectedSupplier(
      order?.supplier
        ? { value: order.supplier.id, label: order.supplier.name, raw: order.supplier }
        : null,
    );
    setEditingId(order.id);
    getPurchaseOrderLinesByPurchaseOrderId(order.id)
      .then(setOrderLines)
      .catch(() => setOrderLines([]));
    setShowModal(true);
  };

  const handleApprove = async (order) => {
    if (!order) return;
    const approved = statusOptions.find((o) => o.label === 'Approved');
    const payload = {
      statusId: approved ? approved.value : null,
      approvedBy: userProfile?.id,
      approvedDate: new Date().toISOString(),
    };
    try {
      await updatePurchaseOrder(order.id, payload);
      setPurchaseOrders(prev =>
        prev.map(po =>
          po.id === order.id
            ? {
              ...po,
              ...payload,
              status: {
                id: approved?.value,
                status: 'Approved',
                colours: {
                  bootstrapName:
                    approved?.colour || po.status?.colours?.bootstrapName,
                },
              },
            }
            : po
        )
      );
      setSelectedOrder(prev =>
        prev && prev.id === order.id
          ? {
            ...prev,
            ...payload,
            status: {
              id: approved?.value,
              status: 'Approved',
              colours: {
                bootstrapName:
                  approved?.colour || prev.status?.colours?.bootstrapName,
              },
            },
          }
          : prev
      );
    } catch (err) {
      console.error('Error approving purchase order', err);
    }
  };

  const handleCancel = async (order) => {
    if (!order) return;
    const cancelled = statusOptions.find(
      (o) => o.label === 'Cancelled' || o.label === 'Canceled',
    );
    const payload = {
      statusId: cancelled ? cancelled.value : 5,
    };
    try {
      await updatePurchaseOrder(order.id, payload);
      setPurchaseOrders(prev =>
        prev.map(po =>
          po.id === order.id
            ? {
              ...po,
              ...payload,
              status: {
                id: payload.statusId,
                status: cancelled?.label || 'Cancelled',
                colours: {
                  bootstrapName:
                    cancelled?.colour || po.status?.colours?.bootstrapName,
                },
              },
            }
            : po
        )
      );
      setSelectedOrder(prev =>
        prev && prev.id === order.id
          ? {
            ...prev,
            ...payload,
            status: {
              id: payload.statusId,
              status: cancelled?.label || 'Cancelled',
              colours: {
                bootstrapName:
                  cancelled?.colour || prev.status?.colours?.bootstrapName,
              },
            },
          }
          : prev
      );
    } catch (err) {
      console.error('Error cancelling purchase order', err);
    }
  };

  const handleRequestCancel = (order) => {
    if (!order) return;
    setOrderToCancel(order);
    setShowCancelModal(true);
  };

  const handleConfirmCancel = async () => {
    if (!orderToCancel) return;
    await handleCancel(orderToCancel);
    setShowCancelModal(false);
    setOrderToCancel(null);
  };

  const handleDismissCancel = () => {
    setShowCancelModal(false);
    setOrderToCancel(null);
  };

  const handleExportPdf = () => {
    if (!selectedOrder) return;
    const org = userProfile.organisation;
    exportPurchaseOrderPdf(org, selectedOrder, selectedOrderLines || []);
  };


  const handleDelete = async (id) => {
    if (window.confirm('Delete purchase order?')) {
      await deletePurchaseOrder(id);
      setPurchaseOrders(prev => prev.filter(p => p.id !== id));
    }
  };

  const generateCsv = () => {
    const headers = ['Number', 'Supplier', 'Order Date', 'Status', 'Total'];
    const rows = purchaseOrders.map((row) => [
      row.poNumber,
      row.supplier?.name || '',
      row.orderDate ? row.orderDate.slice(0, 10) : '',
      row.status?.status,
      row.totalAmount,
    ]);
    const csvLines = [
      headers.join(','),
      ...rows.map((r) => r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')),
    ];
    return csvLines.join('\n');
  };

  const handleExportExcel = () => {
    const headers = ['Number', 'Supplier', 'Order Date', 'Status', 'Total'];
    const rows = purchaseOrders.map((row) => [
      row.poNumber,
      row.supplier?.name || '',
      row.orderDate ? row.orderDate.slice(0, 10) : '',
      row.status?.status,
      row.totalAmount,
    ]);
    const worksheetData = [headers, ...rows];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Purchase Orders');
    const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'purchase_orders.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportCSV = () => {
    const csv = generateCsv();
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'purchase_orders.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const clearForm = () => {
    setNewOrder({});
    setEditingId(null);
    setErrors({});
    setOrderLines([]);
    setEditingLineIndex(null);
    setDeletedLineIds([]);
    setSelectedSupplier(null);
  };

  const closeModal = () => {
    setShowModal(false);
    clearForm();
  };

  const handleSave = async () => {
    const errs = validateForm(inputFields, newOrder);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    const calcTotal = orderLines.length > 0
      ? orderLines.reduce((sum, l) => sum + Number(l.lineTotal || 0), 0)
      : Number(newOrder.totalAmount || 0);
    const payload = { ...newOrder, totalAmount: parseFloat(calcTotal.toFixed(2)) };
    // strip out any null or object values from newAsset response
    const cleanPayload = {};
    for (const key in payload) {
      if (payload[key] !== null && typeof payload[key] !== 'object') {
        cleanPayload[key] = payload[key];
      }
    }
    // append organisation id of the user to the payload
    const purchaseOrderPayload = {
      ...cleanPayload,
    };

    try {
      let refreshId = editingId;
      if (editingId) {
        await updatePurchaseOrder(editingId, purchaseOrderPayload);
        await Promise.all(orderLines.map(l =>
          l.id
            ? updatePurchaseOrderLine(l.id, (() => {
              const { workOrderNumber, workOrderId, ...payloadLine } = l;
              return { ...payloadLine, purchaseOrderId: editingId };
            })())
            : createPurchaseOrderLine((() => {
              const { workOrderNumber, workOrderId, ...payloadLine } = l;
              return { ...payloadLine, purchaseOrderId: editingId };
            })())
        ));
        if (deletedLineIds.length) {
          await Promise.all(deletedLineIds.map(id => deletePurchaseOrderLine(id)));
          setDeletedLineIds([]);
        }
      } else {
        const created = await createPurchaseOrder(purchaseOrderPayload);
        refreshId = created.id;
        await Promise.all(orderLines.map(l => {
          const { workOrderNumber, workOrderId, ...payloadLine } = l;
          return createPurchaseOrderLine({ ...payloadLine, purchaseOrderId: created.id });
        }));
      }
      closeModal();
      await updateOrders();
      if (viewMode === 'panel' && refreshId) {
        try {
          const fresh = await getPurchaseOrderById(refreshId);
          setSelectedOrder(fresh);
          const lines = await getPurchaseOrderLinesByPurchaseOrderId(refreshId);
          setSelectedOrderLines(lines);
        } catch (err) {
          console.error('Error refreshing panel view:', err);
        }
      }
    } catch (err) {
      console.error('Error saving purchase order', err);
    }
  };

  return (
    <>
      {headerSearchContainer &&
        createPortal(
          <input
            type="text"
            className="form-control form-control-sm"
            style={{ width: '200px' }}
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />,
          headerSearchContainer,
        )}
      <div className="card h-100 p-0 rounded-0">
        <div className="card-header d-flex justify-content-between align-items-center border-0 pb-16">
          <div className="d-flex flex-wrap align-items-center gap-2">
            {renderFilterControls()}
          </div>
          <div className="d-flex align-items-center gap-2 ms-auto me-8">
            <div className="dropdown me-2">
              <button
                className="btn btn-outline-secondary text-sm px-12 py-12 radius-4 dropdown-toggle"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                Export Data
              </button>
              <ul className="dropdown-menu p-12 border bg-base shadow">
                <li>
                  <button className="dropdown-item" onClick={handleExportExcel}>
                    Excel
                  </button>
                </li>
                <li>
                  <button className="dropdown-item" onClick={handleExportCSV}>
                    CSV
                  </button>
                </li>
              </ul>
            </div>
            {userProfile?.privileges?.includes('purchase-orders:create') && (
              <button
                className="btn btn-primary text-sm btn-sm px-16 py-12 radius-4 d-flex align-items-center gap-2"
                onClick={async () => {
                  clearForm();
                  setFormReadOnly(false);
                  const today = new Date();
                  const orderDate = today.toISOString().slice(0, 10);
                  const delivery = new Date(today);
                  delivery.setDate(delivery.getDate() + 7);
                  const deliveryDate = delivery.toISOString().slice(0, 10);
                  const draft = statusOptions.find((o) => o.label === 'Draft');
                  setNewOrder({
                    orderDate,
                    deliveryDate,
                    statusId: draft ? draft.value : null,
                  });
                  setShowModal(true);
                }}
              >
                <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" />
                Add Purchase Order
              </button>
            )}
          </div>
        </div>
        <div className="card-body p-0">
        {viewMode === 'table' && (
          <div className="card h-100 p-0 rounded-0">
            <div className="card-body p-0">
              <div
                className="paginated-table-container scroll"
                ref={listContainerRef}
                style={{ maxHeight: 'calc(100vh - 210px)' }}
              >
                <PurchaseOrderDataTable
                  purchaseOrders={purchaseOrders}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
                {poLoading && (
                  <div className="text-center py-2">
                    <span className="spinner-border spinner-border-sm" />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {viewMode === 'panel' && (
          <PurchaseOrderPanelView
            purchaseOrders={purchaseOrders}
            selectedOrder={selectedOrder}
            setSelectedOrder={setSelectedOrder}
            listContainerRef={listContainerRef}
            poLoading={poLoading}
            orderLines={selectedOrderLines}
            notFound={notFound}
            handleEdit={handleEdit}
            handleApprove={handleApprove}
            handleCancel={handleRequestCancel}
            onExportPdf={handleExportPdf}
          />
        )}
      </div>
      {showModal && (
        <div className="modal modal-backdrop show d-block" tabIndex="-1">
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingId ? 'Edit Purchase Order' : 'Add Purchase Order'}</h5>
                <button type="button" className="btn-close" onClick={() => closeModal()} />
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  {inputFields.map(renderField)}
                </div>
                <div className="mt-4">
                  <div className="mt-4">
                    <div className="d-flex justify-content-between align-items-center mt-3 mb-3">
                      <p className="mb-0 fw-bold">Purchase Order Lines</p>
                      {!formReadOnly && (
                        <button className="btn btn-primary btn-sm" onClick={handleAddLine}>
                          + Add Purchase Order Line
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="table-responsive">
                    <table className="table bordered-table dataTable mb-3 w-100">
                      <thead>
                        <tr>
                          <th>Item Code</th>
                          <th>Work Order</th>
                          <th>Description</th>
                          <th>Qty</th>
                          <th>Unit Price</th>
                          <th>Tax %</th>
                          <th>Total</th>
                          <th>Notes</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orderLines.map((ln, idx) => (
                          editingLineIndex === idx ? (
                            <tr key={idx}>
                              <td style={{ width: "150px" }}><input name="itemCode" value={lineForm.itemCode || ''} onChange={handleLineInputChange} className="form-control form-control-sm" /></td>
                              <td style={{ width: "130px" }}>{lineForm.workOrderNumber || ''}</td>
                              <td style={{ width: "200px" }}><input name="description" value={lineForm.description || ''} onChange={handleLineInputChange} className={`form-control form-control-sm ${lineErrors.description ? 'is-invalid' : ''}`} /></td>
                              <td style={{ width: "100px" }}><input name="quantity" type="number" step="1" min="0" value={lineForm.quantity} onChange={handleLineInputChange} className={`form-control form-control-sm ${lineErrors.quantity ? 'is-invalid' : ''}`} /></td>
                              <td style={{ width: "150px" }}><input name="unitPrice" type="number" step="0.01" min="0" value={lineForm.unitPrice} onChange={handleLineInputChange} className={`form-control form-control-sm ${lineErrors.unitPrice ? 'is-invalid' : ''}`} /></td>
                              <td style={{ width: "100px" }}><input name="taxRate" type="number" step="0.1" min="0" value={lineForm.taxRate} onChange={handleLineInputChange} className={`form-control form-control-sm ${lineErrors.taxRate ? 'is-invalid' : ''}`} /></td>
                              <td style={{ width: "80px" }}>{lineForm.lineTotal}</td>
                              <td style={{ width: "200px" }}><input name="notes" value={lineForm.notes || ''} onChange={handleLineInputChange} className="form-control form-control-sm" /></td>
                              <td style={{ width: "100px" }}>
                                <button className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center me-2" onClick={handleSaveLine}><Icon icon="mage:check" /></button>
                                <button className="w-32-px h-32-px bg-secondary-focus text-secondary-main rounded-circle d-inline-flex align-items-center justify-content-center" onClick={handleCancelLine}><Icon icon="mage:multiply" /></button>
                              </td>
                            </tr>
                          ) : (
                            <tr key={idx}>
                              <td>{ln.itemCode}</td>
                              <td>{ln.workOrderNumber || ''}</td>
                              <td>{ln.description}</td>
                              <td>{ln.quantity}</td>
                              <td>{ln.unitPrice}</td>
                              <td>{ln.taxRate}</td>
                              <td>{ln.lineTotal}</td>
                              <td>{ln.notes}</td>
                              <td>
                                {!formReadOnly && (
                                  <>
                                    <button className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center me-2" onClick={() => handleEditLine(idx)}><Icon icon="ic:baseline-edit" /></button>
                                    <button className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center" onClick={() => handleDeleteLine(idx)}><Icon icon="ic:baseline-delete-outline" /></button>
                                  </>
                                )}
                              </td>
                            </tr>
                          )
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => closeModal()}>Close</button>
                {!formReadOnly && (
                  <button className="btn btn-primary" onClick={handleSave}>Save</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {showSupplierModal && (
        <div className="modal modal-backdrop show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add Supplier</h5>
                <button type="button" className="btn-close" onClick={() => setShowSupplierModal(false)} />
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  {supplierInputFields.map(renderSupplierField)}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowSupplierModal(false)}>Close</button>
                <button className="btn btn-primary" onClick={handleSaveSupplier}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showCancelModal && (
        <div className="modal modal-backdrop show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Cancel Purchase Order</h5>
                <button type="button" className="btn-close" onClick={handleDismissCancel} />
              </div>
              <div className="modal-body">
                <p className="mb-0">Do you want to cancel this purchase order?</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleDismissCancel}>Close</button>
                <button type="button" className="btn btn-primary" onClick={handleConfirmCancel}>OK</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  </>
  );
};

export default PurchaseOrderLayer;
